import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ATTRACTIONS } from "../data/content";

export default function AttractionCarousel() {
  const [i, setI] = useState(0);
  const n = ATTRACTIONS.length;
  const timer = useRef(null);
  const touch = useRef(null);

  const go = useCallback((dir) => setI((p) => (p + dir + n) % n), [n]);

  useEffect(() => {
    timer.current = setInterval(() => setI((p) => (p + 1) % n), 5000);
    return () => clearInterval(timer.current);
  }, [n]);

  const reset = () => { clearInterval(timer.current); timer.current = setInterval(() => setI((p) => (p + 1) % n), 5000); };

  const onStart = (e) => { touch.current = (e.touches ? e.touches[0].clientX : e.clientX); };
  const onEnd = (e) => {
    if (touch.current == null) return;
    const end = (e.changedTouches ? e.changedTouches[0].clientX : e.clientX);
    const dx = end - touch.current;
    if (Math.abs(dx) > 50) { go(dx < 0 ? 1 : -1); reset(); }
    touch.current = null;
  };

  return (
    <div data-testid="attraction-carousel" className="relative w-full">
      <div
        className="relative h-[62vh] md:h-[78vh] flex items-center justify-center overflow-hidden select-none"
        onTouchStart={onStart} onTouchEnd={onEnd}
        onMouseDown={onStart} onMouseUp={onEnd}
      >
        {ATTRACTIONS.map((a, idx) => {
          let offset = idx - i;
          if (offset > n / 2) offset -= n;
          if (offset < -n / 2) offset += n;
          const isActive = offset === 0;
          const abs = Math.abs(offset);
          if (abs > 2) return null;
          return (
            <motion.div
              key={a.name}
              className="absolute top-1/2 left-1/2 will-change-transform"
              animate={{
                x: `calc(-50% + ${offset * (isActive ? 0 : 1) * 0}px + ${offset * 62}%)`,
                y: "-50%",
                scale: isActive ? 1 : 0.72,
                opacity: abs > 1 ? 0.25 : isActive ? 1 : 0.55,
                zIndex: 10 - abs,
              }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              style={{ width: "min(78vw, 900px)" }}
            >
              <div className="relative h-[46vh] md:h-[60vh] overflow-hidden grain">
                <img src={a.img} alt={`${a.name}, ${a.place}`} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: isActive ? "linear-gradient(to top, rgba(13,16,56,0.55), transparent 55%)" : "rgba(13,16,56,0.4)" }} />
                {isActive && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.8 }}
                    className="absolute bottom-8 left-8 right-8"
                  >
                    <p className="overline text-gold">{a.place}</p>
                    <h3 className="font-serif text-4xl md:text-6xl text-ivory mt-2">{a.name}</h3>
                  </motion.div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-8 mt-8">
        <button onClick={() => { go(-1); reset(); }} data-testid="carousel-prev" aria-label="Previous" className="text-ivory/70 hover:text-gold transition-colors"><ArrowLeft size={22} /></button>
        <div className="flex gap-2">
          {ATTRACTIONS.map((_, idx) => (
            <button key={idx} onClick={() => { setI(idx); reset(); }} aria-label={`Go to slide ${idx + 1}`}
              className="h-[3px] transition-all duration-500" style={{ width: idx === i ? 34 : 14, background: idx === i ? "#D4AF37" : "rgba(248,247,243,0.3)" }} />
          ))}
        </div>
        <button onClick={() => { go(1); reset(); }} data-testid="carousel-next" aria-label="Next" className="text-ivory/70 hover:text-gold transition-colors"><ArrowRight size={22} /></button>
      </div>
    </div>
  );
}
